import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import ContactHeader from './components/ContactHeader';
import ContactInformation from './components/ContactInformation';
import StageManagement from './components/StageManagement';
import Timeline from '../../components/Timeline';
import ProfileEditor from './components/ProfileEditor';
import QuickActions from './components/QuickActions';
import RelationshipMap from './components/RelationshipMap';

import PropertiesModal from '../../components/ui/PropertiesModal';
import CreateTaskModal from '../create-task-modal';
import { contactsService } from '../../services/contactsService';
import { timelineService } from '../../services/timelineService';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const ContactDetails = () => {
  const { id: contactId } = useParams();
  const navigate = useNavigate();
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('timeline');
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [timelineItems, setTimelineItems] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(true);
  const [relatedContacts, setRelatedContacts] = useState([]);
  const [showPropertiesModal, setShowPropertiesModal] = useState(false);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);

  // Enhanced parameter validation with better error handling
  useEffect(() => {
    if (!contactId) {
      console.error('No contact ID provided in URL');
      setError('Contact ID is required');
      setLoading(false);
      return;
    }

    // Validate UUID format
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (!uuidRegex?.test(contactId)) {
      console.error('Invalid contact ID format in URL:', contactId);
      setError('Invalid contact ID format');
      setLoading(false);
      return;
    }

    // Load contact details + timeline
    loadContactDetails();
    loadTimeline();
  }, [contactId]);

  // Enhanced loadContactDetails with better error handling
  const loadContactDetails = async () => {
    setLoading(true);
    setError(null);

    try {
      // Call the actual contacts service to get real data from database
      const result = await contactsService?.getContact(contactId);
      
      if (result?.success && result?.data) {
        const propertyInfo = result?.data?.property ? {
          id: result?.data?.property?.id,
          name: result?.data?.property?.name || 'Unnamed Property',
          address: result?.data?.property?.address || '',
          city: result?.data?.property?.city || '',
          state: result?.data?.property?.state || '',
          buildingType: result?.data?.property?.building_type || '',
          stage: result?.data?.property?.stage || ''
        } : null;

        // Transform database data to match the expected format
        const contactData = {
          id: result?.data?.id,
          name: `${result?.data?.first_name || ''} ${result?.data?.last_name || ''}`?.trim(),
          firstName: result?.data?.first_name || '',
          lastName: result?.data?.last_name || '',
          email: result?.data?.email || '',
          phone: result?.data?.phone || '',
          mobilePhone: result?.data?.mobile_phone || '',
          role: result?.data?.title || 'Contact',
          title: result?.data?.title || '',
          account: result?.data?.account?.name || 'Unknown Account',
          accountId: result?.data?.account_id,
          property: propertyInfo?.name || null,
          propertyId: propertyInfo?.id || null,
          propertyDetails: propertyInfo,
          propertyAddress: propertyInfo
            ? [propertyInfo?.address, propertyInfo?.city, propertyInfo?.state]?.filter(Boolean)?.join(', ')
            : '',
          propertyStage: propertyInfo?.stage || '',
          stage: result?.data?.stage || 'Identified',
          lastInteraction: result?.data?.updated_at ? new Date(result?.data?.updated_at) : new Date(result?.data?.created_at),
          createdAt: new Date(result?.data?.created_at),
          updatedAt: new Date(result?.data?.updated_at || result?.data?.created_at),
          isPrimaryContact: result?.data?.is_primary_contact || false,
          notes: result?.data?.notes || '',
          createdById: result?.data?.created_by || null,
          createdBy: result?.data?.created_by || null,
          // Additional fields that might not be in database - provide fallbacks
          linkedInUrl: '',
          companyWebsite: result?.data?.account?.website || '',
          address: result?.data?.account?.address || '',
          department: '',
          reportsTo: '',
          decisionMakingAuthority: 'Unknown',
          preferredCommunication: 'Email',
          timeZone: 'EST'
        };

        setContact(contactData);
        console.log('Contact loaded successfully:', contactData);
        
        // Set empty related contacts for now (could be enhanced later)
        setRelatedContacts([]);
      } else {
        throw new Error(result?.error || 'Contact not found');
      }
    } catch (err) {
      console.error('Error loading contact:', err);
      setError(err?.message || 'Failed to load contact details');
    } finally {
      setLoading(false);
    }
  };

  const loadTimeline = async () => {
    if (!contactId) return;
    setTimelineLoading(true);

    try {
      const result = await timelineService?.getTimelineForEntity('contact', contactId);
      if (result?.success) {
        setTimelineItems(result?.data || []);
      } else {
        console.error('Failed to load timeline:', result?.error);
        setTimelineItems([]);
      }
    } catch (err) {
      console.error('Error loading timeline:', err);
      setTimelineItems([]);
    } finally {
      setTimelineLoading(false);
    }
  };

  const handleStageUpdate = async (newStage) => {
    try {
      // Update the database with the new stage
      const result = await contactsService?.updateContact(contactId, { stage: newStage });
      
      if (result?.success) {
        setContact(prev => ({ ...prev, stage: newStage, updatedAt: new Date() }));
      } else {
        throw new Error(result?.error || 'Failed to update stage');
      }
    } catch (err) {
      console.error('Failed to update stage:', err);
      // Show error to user - in a real app, you'd want a toast notification
      alert('Failed to update contact stage. Please try again.');
    }
  };

  const handleProfileUpdate = async (updates) => {
    try {
      // Convert UI format back to database format
      const dbUpdates = {
        first_name: updates?.firstName,
        last_name: updates?.lastName,
        email: updates?.email,
        phone: updates?.phone,
        mobile_phone: updates?.mobilePhone,
        title: updates?.title,
        notes: updates?.notes,
        stage: updates?.stage,
        created_by: updates?.createdBy
      };

      // Remove undefined values
      Object.keys(dbUpdates)?.forEach(key => {
        if (dbUpdates?.[key] === undefined) {
          delete dbUpdates?.[key];
        }
      });

      const result = await contactsService?.updateContact(contactId, dbUpdates);
      
      if (result?.success) {
        setContact(prev => ({ ...prev, ...updates, updatedAt: new Date() }));
        setIsEditingProfile(false);
      } else {
        throw new Error(result?.error || 'Failed to update contact');
      }
    } catch (err) {
      console.error('Failed to update profile:', err);
      // Show error to user - in a real app, you'd want a toast notification
      alert('Failed to update contact profile. Please try again.');
    }
  };

  const handleContactUpdate = () => {
    // Reload contact data when updates occur
    loadContactDetails();
  };

  const handleActivityLog = (activityData) => {
    navigate('/log-activity', { 
      state: { 
        preselected: true,
        contactId: contact?.id,
        contactName: contact?.name,
        accountId: contact?.accountId,
        accountName: contact?.account,
        contact: {
          value: contact?.id,
          label: contact?.name,
          description: `${contact?.title || 'Contact'} at ${contact?.account}`
        },
        account: {
          value: contact?.accountId,
          label: contact?.account,
          description: 'Account'
        }
      }
    });
  };

  const handlePhoneCall = (phoneNumber) => {
    window.open(`tel:${phoneNumber}`, '_self');
  };

  const handleEmail = (emailAddress) => {
    window.open(`mailto:${emailAddress}`, '_self');
  };

  const handleNavigateToAccount = () => {
    if (contact?.accountId) {
      navigate(`/accounts/${contact?.accountId}`);
    }
  };

  const handleNavigateToProperty = (propertyId) => {
    if (propertyId) {
      navigate(`/properties/${propertyId}`);
    }
  };

  const handleTaskCreated = (newTask) => {
    console.log('New task created:', newTask);
    setShowCreateTaskModal(false);
    loadTimeline();
  };

  const handleCreateTask = () => {
    setShowCreateTaskModal(true);
  };

  // Enhanced loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header onMenuToggle={() => {}} />
        <div className="pt-16 px-4 py-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading contact details...</p>
                <p className="text-sm text-muted-foreground mt-2">ID: {contactId}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Enhanced error state
  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header onMenuToggle={() => {}} />
        <div className="pt-16 px-4 py-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center py-12">
              <div className="mb-6">
                <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="AlertTriangle" size={24} className="text-destructive" />
                </div>
                <h2 className="text-xl font-semibold text-foreground mb-2">Unable to Load Contact</h2>
                <p className="text-destructive mb-4">{error}</p>
                <p className="text-sm text-muted-foreground mb-6">Contact ID: {contactId}</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  variant="outline"
                  onClick={() => loadContactDetails()}
                >
                  <Icon name="RefreshCw" size={16} className="mr-2" />
                  Try Again
                </Button>
                <Button
                  onClick={() => navigate('/contacts')}
                >
                  <Icon name="ArrowLeft" size={16} className="mr-2" />
                  Back to Contacts
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Enhanced not found state
  if (!contact) {
    return (
      <div className="min-h-screen bg-background">
        <Header onMenuToggle={() => {}} />
        <div className="pt-16 px-4 py-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center py-12">
              <div className="mb-6">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="UserX" size={24} className="text-muted-foreground" />
                </div>
                <h2 className="text-xl font-semibold text-foreground mb-2">Contact Not Found</h2>
                <p className="text-muted-foreground mb-4">The requested contact could not be found.</p>
                <p className="text-sm text-muted-foreground mb-6">ID: {contactId}</p>
              </div>
              <Button
                onClick={() => navigate('/contacts')}
              >
                <Icon name="ArrowLeft" size={16} className="mr-2" />
                Back to Contacts List
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header onMenuToggle={() => {}} />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
          {/* Contact Header */}
          <ContactHeader 
            contact={contact}
            onNavigateToAccount={handleNavigateToAccount}
            onNavigateToProperty={handleNavigateToProperty}
            onEdit={() => setIsEditingProfile(true)}
          />

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Contact Information & Stage Management */}
            <div className="lg:col-span-1 space-y-6">
              <ContactInformation 
                contact={contact}
                onPhoneCall={handlePhoneCall}
                onEmail={handleEmail}
              />
              
              <StageManagement 
                currentStage={contact?.stage}
                lastInteraction={contact?.lastInteraction}
                onStageUpdate={handleStageUpdate}
              />

              <QuickActions 
                contact={contact}
                onActivityLog={handleActivityLog}
                onPhoneCall={() => handlePhoneCall(contact?.phone)}
                onEmail={() => handleEmail(contact?.email)}
                onContactUpdate={handleContactUpdate}
              />
            </div>

            {/* Right Column - Activities & Relationships */}
            <div className="lg:col-span-2 space-y-6">
              {/* Tab Navigation */}
              <div className="bg-card rounded-lg border border-border">
                <div className="border-b border-border px-6 py-4">
                  <div className="flex space-x-8">
                    <button
                      onClick={() => setActiveTab('timeline')}
                      className={`text-sm font-medium pb-2 border-b-2 transition-colors ${
                        activeTab === 'timeline' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      Timeline ({timelineItems?.length || 0})
                    </button>
                    <button
                      onClick={() => setActiveTab('relationships')}
                      className={`text-sm font-medium pb-2 border-b-2 transition-colors ${
                        activeTab === 'relationships' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      Relationships ({relatedContacts?.length || 0})
                    </button>
                    <button
                      onClick={() => setShowPropertiesModal(true)}
                      className="text-sm font-medium pb-2 border-b-2 border-transparent text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <Icon name="Building2" size={16} className="mr-1 inline" />
                      Properties
                    </button>
                  </div>
                </div>

                {/* Tab Content */}
                <div className="p-6">
                  {activeTab === 'timeline' && (
                    <Timeline
                      title="Timeline"
                      items={timelineItems}
                      loading={timelineLoading}
                      onLogActivity={handleActivityLog}
                      onCreateTask={handleCreateTask}
                      onRefresh={loadTimeline}
                    />
                  )}
                  
                  {activeTab === 'relationships' && (
                    <RelationshipMap 
                      contacts={relatedContacts}
                      currentContact={contact}
                    />
                  )}

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Task Modal */}
      <CreateTaskModal
        isOpen={showCreateTaskModal}
        onClose={() => setShowCreateTaskModal(false)}
        onTaskCreated={handleTaskCreated}
        preSelectedEntity={{
          type: 'contact',
          id: contact?.id,
          contactName: contact?.name,
          accountName: contact?.account,
          accountId: contact?.accountId
        }}
      />

      {/* Profile Editor Modal */}
      {isEditingProfile && (
        <ProfileEditor 
          contact={contact}
          onSave={handleProfileUpdate}
          onCancel={() => setIsEditingProfile(false)}
        />
      )}

      {/* Properties Modal */}
      <PropertiesModal
        isOpen={showPropertiesModal}
        onClose={() => setShowPropertiesModal(false)}
        contact={contact}
        onNavigateToProperty={handleNavigateToProperty}
      />
    </div>
  );
};

export default ContactDetails;
